/**
 * What can be measured about a draft, measured before anybody reads it.
 *
 * Nothing here calls a model. Every check is arithmetic over the text, which
 * means it is instant, free, and identical every time - and that is the point:
 * a writer should not read four thousand characters to discover that the target
 * keyword is missing, that two sections open with the same sentence, or that the
 * closing 「いかがでしたか」 the prompt bans slipped through anyway.
 *
 * The legal check stays what it is: separate, model-assisted, and about the law.
 * This is about the craft.
 *
 * A finding is worth adding here only if it is BOTH mechanical and actionable.
 * "The tone is a bit flat" is neither. "この記事の狙うキーワードが本文に一度も
 * 出てきません" is both.
 */
import { parseHeadings } from './outline.ts';
import { visibleLength } from '../../shared/rich-format.ts';
import { keywordPresence } from '../../shared/keywords.ts';

export type CheckLevel = 'error' | 'warn' | 'ok';

export interface CheckFinding {
  id: string;
  level: CheckLevel;
  title: string;
  detail: string;
  /**
   * The words this is about, so the editor can go to them.
   *
   * Text rather than an offset: the body is edited as a document now, and a
   * character offset into the Markdown would have to be translated into the
   * document's own positions - where an off-by-one selects the wrong sentence.
   * Absent when a finding has no location, which is what makes a missing keyword
   * missing.
   */
  quote?: string;
}

export interface CheckInput {
  body: string;
  /**
   * The article's keywords, in order.
   *
   * The FIRST is the one it is written to rank for and is checked hardest; the
   * rest only have to appear. One ordered list rather than two fields - see
   * migration 050.
   */
  keywords: readonly string[];
  /** Total characters aimed at, when the article or clinic set one. */
  targetChars: number | null;
  /** The store's reservation URL, so a CTA can be checked for a real link. */
  reservationUrl: string | null;
  /**
   * The rows editorial_checks holds. REQUIRED, no default - see the note on
   * EditorialCheckRow above. An empty array is a legitimate answer (every check
   * was disabled or deleted) and must mean exactly that: none of these findings
   * fire, not that a compiled list took over.
   */
  editorialChecks: readonly EditorialCheckRow[];
}

/**
 * One row of editorial_checks (migration 096), compiled and ready to test.
 *
 * NO LIST IS HARDCODED HERE ANY MORE. There used to be one - TELLS, eight
 * phrasings compiled into this file - and it had exactly the problem
 * ai/lint.ts's COMPILED_CHECKS had: a pattern nobody on the JMC side could see,
 * edit, or turn off, flagging a phrase in a highlighted quote with nothing to
 * point to when somebody asked why. See AdminPromptChecks and migration 096's
 * comment for the fuller version of this argument.
 */
export interface EditorialCheckRow {
  code: string;
  kind: 'TELL' | 'COMPARABLE_HEADING';
  label: string;
  pattern: string;
  flags: string;
  enabled: boolean;
}

interface CompiledTell {
  code: string;
  label: string;
  /** Always carries 'g': every caller here uses matchAll. */
  pattern: RegExp;
}

/**
 * Rows into regexes, skipping one that fails to compile rather than taking the
 * whole check down - the same rule prompt-checks.ts and rulebook.ts use for
 * stored patterns.
 */
function compileTells(rows: readonly EditorialCheckRow[]): CompiledTell[] {
  const out: CompiledTell[] = [];
  for (const row of rows) {
    if (row.kind !== 'TELL' || !row.enabled) continue;
    try {
      out.push({ code: row.code, label: row.label, pattern: new RegExp(row.pattern, `${row.flags}g`) });
    } catch {
      // Reported nowhere on this free, client-only check; an operator sees the
      // save refused at the boundary (admin/routes.ts) before a bad row here.
    }
  }
  return out;
}

function compileComparableHeadings(rows: readonly EditorialCheckRow[]): RegExp | null {
  const parts = rows
    .filter((row) => row.kind === 'COMPARABLE_HEADING' && row.enabled)
    .map((row) => row.pattern);
  if (parts.length === 0) return null;
  try {
    return new RegExp(parts.map((part) => `(?:${part})`).join('|'));
  } catch {
    return null;
  }
}

/** Headings that promise a comparison, and therefore a table. */
/**
 * Sentences that appear more than once.
 *
 * Split on Japanese full stops and trimmed; anything under twenty characters is
 * ignored, because 「詳しくはこちら」 twice is not a defect. Section drafting made
 * this rarer and not impossible: two sections can still arrive at the same
 * explanation from different directions.
 */
function repeatedSentences(body: string): Array<{ text: string; at: number }> {
  const seen = new Map<string, number>();
  const repeats: Array<{ text: string; at: number }> = [];
  let cursor = 0;

  for (const raw of body.split(/(?<=。)/)) {
    /*
     * Heading lines are dropped from the sentence, not from the offset.
     *
     * A segment that follows a heading carries that heading with it, so two
     * identical sentences sitting under different headings compared as
     * different strings and the check silently found nothing.
     */
    const text = raw.replace(/^\s*#{1,6}\s+.*$/gm, '').trim();
    const at = cursor;
    cursor += raw.length;
    if (text.length < 20) continue;
    if (seen.has(text)) {
      if (!repeats.some((entry) => entry.text === text)) repeats.push({ text, at });
    } else {
      seen.set(text, at);
    }
  }
  return repeats;
}

/**
 * Run every check.
 *
 * Ordered by what a writer should deal with first: what the article is missing,
 * then what it says badly, then what it merely could improve. `ok` findings are
 * returned too - a panel that only ever shows problems teaches nobody what was
 * checked, and a clean article deserves to look clean rather than empty.
 */
export function reviewDraft(input: CheckInput): CheckFinding[] {
  const { body, keywords, targetChars, reservationUrl, editorialChecks } = input;
  const tells = compileTells(editorialChecks);
  const comparableHeadingPattern = compileComparableHeadings(editorialChecks);
  const targetKeyword = keywords[0] ?? '';
  // The rest still have to appear; the primary one is checked separately above.
  const requiredKeywords = keywords.slice(1);
  const findings: CheckFinding[] = [];
  /*
   * The words, not the markup.
   *
   * `body.trim().length` counted every `<table style="min-width: 100px;">` and
   * every `<div class="callout…">` the editor stores, so one article reported
   * 9,224 characters here and on the outline rail while the writing and final
   * screens - which already strip HTML - said 4,431. More than half of that
   * number was table markup with about a thousand characters of text in it.
   *
   * The disagreement was not the harm. 文字数 warned 「目安より長めです」 about an
   * article that was in fact under its target, which is the check telling a
   * writer to cut paragraphs that are the right length. One definition, in
   * shared/rich-format.ts, for every screen that counts.
   */
  const chars = visibleLength(body);
  const headings = parseHeadings(body);

  // --- what it must contain -------------------------------------------------
  if (targetKeyword.trim()) {
    /*
     * Matched on the WORDS, not on the stored string.
     *
     * `count(body, keyword)` was an exact substring test against the body with
     * its markup in it, and it reported 「本文にありません」 for articles the
     * keyword was all over: bolded inside the phrase, typed full-width in one
     * place and half-width in the other, or - for a two-word long-tail keyword -
     * written the way Japanese actually joins the two words. See
     * keywordPresence in shared/keywords.ts for each of the three.
     */
    const found = keywordPresence(body, targetKeyword);
    const hits = found.hits;
    const inTitle = headings.some(
      (heading) => keywordPresence(heading.text, targetKeyword).hits > 0,
    );
    if (hits === 0 && found.parts === 'all') {
      /*
       * The phrase is there, in the article's own grammar - 「ダーマペン4の
       * ダウンタイム」 for 「ダーマペン4 ダウンタイム」. Not a defect and not silence
       * either: a writer checking their keywords should see that this one was
       * looked for and what was found, which is the difference between a panel
       * that is trusted and one that is clicked past.
       */
      findings.push({
        id: 'target-split',
        level: 'ok',
        title: '狙うキーワード',
        detail: `「${targetKeyword}」は語順を変えて本文に含まれています。`,
      });
    } else if (hits === 0) {
      findings.push({
        id: 'target-missing',
        level: 'error',
        title: '狙うキーワードが本文にありません',
        detail: `「${targetKeyword}」が一度も出てきません。検索で見つかりません。`,
      });
    } else if (hits > Math.max(6, Math.round(chars / 400))) {
      /*
       * Stuffing, flagged as well as absence. A keyword every two hundred
       * characters reads as machine-written and is the older SEO failure mode,
       * so both ends of the range are worth naming.
       */
      findings.push({
        id: 'target-stuffed',
        level: 'warn',
        title: '狙うキーワードが多すぎます',
        detail: `「${targetKeyword}」が${hits}回。文章として不自然に見えます。`,
        quote: targetKeyword.trim(),
      });
    } else {
      findings.push({
        id: 'target-ok',
        level: 'ok',
        title: '狙うキーワード',
        detail: `「${targetKeyword}」が${hits}回${inTitle ? '、見出しにも含まれています' : ''}。`,
      });
    }
  }

  /*
   * The same matching for the rest of the list. This was the loudest version of
   * the bug - one 「本文にないもの: …」 line naming keywords that were in the
   * article, every time, which is how a panel stops being read.
   */
  const missingKeywords = requiredKeywords.filter((keyword) => {
    if (keyword.trim() === '') return false;
    const found = keywordPresence(body, keyword);
    return found.hits === 0 && found.parts !== 'all';
  });
  if (missingKeywords.length > 0) {
    findings.push({
      id: 'keywords-missing',
      level: 'warn',
      title: '必須キーワードが不足しています',
      detail: `本文にないもの: ${missingKeywords.join('、')}`,
    });
  } else if (requiredKeywords.length > 0) {
    findings.push({
      id: 'keywords-ok',
      level: 'ok',
      title: '必須キーワード',
      detail: `${requiredKeywords.length}件すべてが本文にあります。`,
    });
  }

  // --- length ---------------------------------------------------------------
  if (targetChars) {
    const off = (chars - targetChars) / targetChars;
    findings.push({
      id: 'length',
      level: Math.abs(off) > 0.3 ? 'warn' : 'ok',
      title: '文字数',
      detail: `${chars.toLocaleString('ja-JP')}文字（目安 ${targetChars.toLocaleString('ja-JP')}）${
        Math.abs(off) > 0.3 ? (off < 0 ? '。目安より短めです' : '。目安より長めです') : ''
      }`,
    });
  }

  // --- how it reads ---------------------------------------------------------
  for (const tell of tells) {
    const matches = [...body.matchAll(tell.pattern)];
    if (matches.length === 0) continue;
    findings.push({
      id: `tell-${tell.code}`,
      level: 'warn',
      title: 'AIらしい言い回しが残っています',
      detail: `${tell.label}が${matches.length}回。指示では禁止している表現です。`,
      quote: matches[0]![0],
    });
  }

  /*
   * Two sections opening the same way.
   *
   * The single-call generation did this constantly - every H2 began with a
   * sympathy sentence - and it is the clearest sign a reader gets that nobody
   * wrote this. Compared on the first fifteen characters after the heading.
   */
  const openings = new Map<string, string>();
  for (const heading of headings) {
    /*
     * From the end of the heading LINE, not the end of the section: a section
     * runs to the next heading, so slicing from there reads the section after
     * this one and every comparison is against the wrong text.
     */
    const lineEnd = body.indexOf('\n', heading.start);
    const from = lineEnd === -1 ? heading.end : lineEnd + 1;
    const after = body.slice(from, from + 120).trim();
    const opening = after.slice(0, 15);
    if (opening.length < 10) continue;
    const previous = openings.get(opening);
    if (previous) {
      findings.push({
        id: `opening-${heading.text}`,
        level: 'warn',
        title: '同じ書き出しが繰り返されています',
        detail: `「${previous}」と「${heading.text}」が同じ書き出しです。`,
        quote: heading.text,
      });
    } else {
      openings.set(opening, heading.text);
    }
  }

  for (const repeat of repeatedSentences(body)) {
    findings.push({
      id: `repeat-${repeat.at}`,
      level: 'warn',
      title: '同じ文が繰り返されています',
      detail: repeat.text.slice(0, 40),
      quote: repeat.text,
    });
  }

  // --- structure ------------------------------------------------------------
  const comparable = comparableHeadingPattern
    ? headings.filter((heading) => comparableHeadingPattern.test(heading.text))
    : [];
  const withoutTable = comparable.filter(
    (heading) => !body.slice(heading.start, heading.end).includes('|'),
  );
  if (withoutTable.length > 0) {
    findings.push({
      id: 'no-table',
      level: 'warn',
      title: '比較の節に表がありません',
      detail: `${withoutTable.map((heading) => heading.text).join('、')}：並べて示すと読みやすくなります。`,
      quote: withoutTable[0]!.text,
    });
  }

  /*
   * A CTA that links somewhere. The reservation URL is the one link that decides
   * whether the article does its job, and an article ending in 「ご相談ください」
   * with no link is the commonest way to waste one.
   */
  if (reservationUrl) {
    findings.push(
      body.includes(reservationUrl)
        ? { id: 'cta-ok', level: 'ok', title: 'CTA', detail: '予約ページへのリンクがあります。' }
        : {
            id: 'cta-missing',
            level: 'error',
            title: '予約ページへのリンクがありません',
            detail: '「差し込み」からCTAを入れてください。',
          },
    );
  }

  const longWithoutH3 = headings
    .filter((heading) => heading.level === 2)
    .filter((heading) => {
      const text = body.slice(heading.start, heading.end);
      // Counted the same way: a section is long when a READER has 900 characters
      // to get through, not when its stored markup adds up to that.
      return visibleLength(text) > 900 && !/\n###\s/.test(text);
    });
  if (longWithoutH3.length > 0) {
    findings.push({
      id: 'no-h3',
      level: 'warn',
      title: '長い節に小見出しがありません',
      detail: `${longWithoutH3.map((heading) => heading.text).join('、')}：H3で区切ると読みやすくなります。`,
      quote: longWithoutH3[0]!.text,
    });
  }

  return findings;
}

/** How many findings actually need doing. */
export const problemCount = (findings: readonly CheckFinding[]): number =>
  findings.filter((finding) => finding.level !== 'ok').length;
